// World Cup stadium/shootout physics — self-contained box3d runtime.
//
// Phase 3b: ported onto the three-box3d library (`box3d-web`). The hand-rolled
// bridge + inline accumulator are replaced by the library's World API +
// FixedStepper; the read path is backed by a TransformBuffer. The runtime's
// public shape (rt.records + rt.transforms flat layout, intro/kick state,
// __cupState contract) is unchanged so the render layer and E2E are untouched.
// Sensors genuinely work on the round-2 wasm (enableSensorEvents on solid
// shapes), though this page only consumes contact-begin events.

import {
  createBox3D,
  FixedStepper,
  TransformBuffer,
  type Box3D,
  type World,
  type BodyHandle,
} from 'box3d-web';

export const PHYSICS_FIXED_DT = 1 / 60;
export const FAN_BALL_LIMIT = 12;

const INTRO_ASSEMBLE_SECONDS = 0.52;
const INTRO_HOLD_SECONDS = 1.5;
const INTRO_FADE_SECONDS = 0.9;
const MATCH_BALL_RADIUS = 0.28;
const PLAYER_BASE_Y = 0.35;
const PLAYER_BODY_CENTER_OFFSET_Y = 0.42;
const KICK_LUNGE_SECONDS = 0.45;
const KICK_STRIKE_SECONDS = 0.08;
const KICK_RECOVER_SECONDS = 0.6;
const FAN_BALL_COLORS = ['#ff3f70', '#27e8ff', '#ffe45c', '#49ff8f', '#8d72ff', '#ff8f2f', '#ffffff'];

export type PhysicsRecordKind = 'introBox' | 'matchBall' | 'fanBall';
export type IntroPhase = 'assembling' | 'holding' | 'collapsing' | 'done';
export type KickPhase = 'idle' | 'lunge' | 'strike' | 'recover';

export interface StadiumPlayerLayout {
  x: number;
  z: number;
  rotation: number;
  phase: number;
  side: 'home' | 'away';
}

interface IntroBoxSpec {
  startX: number;
  startY: number;
  startZ: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  hx: number;
  hy: number;
  hz: number;
  delay: number;
  color: string;
}

interface IntroBoxBody extends IntroBoxSpec {
  bodyId: number;
  dynamic: boolean;
  currentX: number;
  currentY: number;
  currentZ: number;
  currentQX: number;
  currentQY: number;
  currentQZ: number;
  currentQW: number;
}

export interface CupKickState {
  active: boolean;
  playerIndex: number;
  phase: KickPhase;
  startedAt: number;
  strikeAt: number;
  recoverStartedAt: number;
  recoverFromX: number;
  recoverFromZ: number;
  recoverFromYaw: number;
  strikeApplied: boolean;
  nearestPlayer: { x: number; y: number; z: number };
  ballAtStrike: { x: number; y: number; z: number } | null;
}

export interface FanBallBody {
  bodyId: number;
  radius: number;
  color: string;
  pulse: number;
}

export interface PhysicsReadRecord {
  bodyId: number;
  kind: PhysicsRecordKind;
  index: number;
}

export interface CupPhysicsRuntime {
  world: World;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  elapsed: number;
  fixedTime: number;
  randomSeed: number;
  introBoxes: IntroBoxBody[];
  introFade: number;
  introPhase: IntroPhase;
  introFadeStarted: boolean;
  introFadeStartedAt: number;
  introBodiesDestroyed: boolean;
  lineupVisible: boolean;
  ambientReady: boolean;
  playerBodies: number[];
  matchBallBody: number;
  matchBallPulse: number;
  matchBallPosition: { x: number; y: number; z: number };
  fanBalls: FanBallBody[];
  records: PhysicsReadRecord[];
  bodyIds: Int32Array;
  transforms: Float32Array;
  idsDirty: boolean;
  nextKickAt: number;
  kick: CupKickState;
}

const lineupXs = [-4, -2, 0, 2, 4];

export const STADIUM_PLAYER_LAYOUT: StadiumPlayerLayout[] = [
  ...lineupXs.map((x, i) => ({ x, z: -2.25, rotation: 0, phase: i * 0.7, side: 'home' as const })),
  ...lineupXs.map((x, i) => ({ x, z: 2.25, rotation: Math.PI, phase: i * 0.62 + 1.5, side: 'away' as const })),
];

const INTRO_BOX_SPECS = buildIntroBoxSpecs();
const INTRO_ALL_ARRIVED_AT = INTRO_BOX_SPECS.reduce((latest, box) => Math.max(latest, box.delay + INTRO_ASSEMBLE_SECONDS), 0);
const INTRO_HOLD_START = INTRO_ALL_ARRIVED_AT + 0.12;
const INTRO_COLLAPSE_START = INTRO_HOLD_START + INTRO_HOLD_SECONDS;
export const INTRO_BOX_COUNT = INTRO_BOX_SPECS.length;

let modulePromise: Promise<Box3D> | null = null;
let runtimePromise: Promise<CupPhysicsRuntime> | null = null;
let runtime: CupPhysicsRuntime | null = null;
let runtimeFailed = false;

function loadModule() {
  if (!modulePromise) {
    modulePromise = createBox3D();
  }
  return modulePromise;
}

export function getExistingCupPhysicsRuntime() {
  return runtime;
}

export async function getCupPhysicsRuntime() {
  if (runtime) return runtime;
  if (runtimeFailed) throw new Error('Cup physics failed to initialize');

  if (!runtimePromise) {
    runtimePromise = createCupPhysicsRuntime()
      .then((created) => {
        runtime = created;
        return created;
      })
      .catch((error: unknown) => {
        runtimeFailed = true;
        runtimePromise = null;
        throw error;
      });
  }

  return runtimePromise;
}

export function advanceCupPhysics(rt: CupPhysicsRuntime, delta: number) {
  const clampedDelta = Math.min(Math.max(delta, 0), 0.1);
  rt.elapsed += clampedDelta;

  spawnDueIntroBoxes(rt);
  updateIntroLifecycle(rt);

  const stepped = rt.stepper.advance(clampedDelta, (dt) => {
    updateIntroKinematicTargets(rt, dt);

    if (rt.ambientReady) {
      updateKickChoreography(rt);
      updatePlayerTargets(rt, dt);
    }

    rt.world.step(dt, 4);
    rt.fixedTime += dt;
  });

  if (stepped) {
    pulseContactedBalls(rt);
  }

  rt.matchBallPulse = Math.max(0, rt.matchBallPulse - clampedDelta * 2.8);
  rt.fanBalls.forEach((ball) => {
    ball.pulse = Math.max(0, ball.pulse - clampedDelta * 2.4);
  });

  if (rt.idsDirty) {
    rebuildReadBuffers(rt);
  }
  if (rt.bodyIds.length > 0) {
    rt.buffer.readInto(rt.world);
    updateCachedIntroBoxTransforms(rt);
    updateCachedMatchBallPosition(rt);
  }
}

export function spawnFanBall(rt: CupPhysicsRuntime, point: { x: number; y: number; z: number }) {
  const radius = 0.38 + nextRandom(rt) * 0.08;
  const bodyId = rt.world.createBody({
    type: 'dynamic',
    position: [clamp(point.x, -16, 16), Math.max(point.y, 0.1) + 12, clamp(point.z, -20, 20)],
    ccd: true,
  });
  rt.world.addSphere(bodyId, radius, { density: 0.55, friction: 0.52, restitution: 0.62 });

  rt.fanBalls.push({
    bodyId,
    radius,
    color: FAN_BALL_COLORS[Math.floor(nextRandom(rt) * FAN_BALL_COLORS.length) % FAN_BALL_COLORS.length],
    pulse: 0.28,
  });

  while (rt.fanBalls.length > FAN_BALL_LIMIT) {
    const oldest = rt.fanBalls.shift();
    if (oldest) {
      rt.world.destroyBody(oldest.bodyId as BodyHandle);
    }
  }

  rt.idsDirty = true;
  return rt.fanBalls.length;
}

export function stadiumPlayerGroupY(layout: StadiumPlayerLayout, elapsed: number) {
  return PLAYER_BASE_Y + Math.sin(elapsed * 2.4 + layout.phase) * 0.045;
}

async function createCupPhysicsRuntime(): Promise<CupPhysicsRuntime> {
  const box3d = await loadModule();
  const world = box3d.createWorld({ gravity: [0, -9.8, 0] });
  const created: CupPhysicsRuntime = {
    world,
    // The old inline accumulator capped the backlog at 0.1s (up to 6 fixed
    // steps per frame) — mirror that rather than the library default of 3.
    stepper: new FixedStepper({ fixedDt: PHYSICS_FIXED_DT, substeps: 4, maxDeltaClamp: 0.1, maxStepsPerFrame: 6 }),
    buffer: new TransformBuffer(INTRO_BOX_SPECS.length + FAN_BALL_LIMIT + 1),
    elapsed: 0,
    fixedTime: 0,
    randomSeed: 0x26c0ffee,
    introBoxes: INTRO_BOX_SPECS.map((box) => ({
      ...box,
      bodyId: 0,
      dynamic: false,
      currentX: box.startX,
      currentY: box.startY,
      currentZ: box.startZ,
      currentQX: 0,
      currentQY: 0,
      currentQZ: 0,
      currentQW: 1,
    })),
    introFade: 1,
    introPhase: 'assembling',
    introFadeStarted: false,
    introFadeStartedAt: 0,
    introBodiesDestroyed: false,
    lineupVisible: false,
    ambientReady: false,
    playerBodies: [],
    matchBallBody: 0,
    matchBallPulse: 0,
    matchBallPosition: { x: 0, y: MATCH_BALL_RADIUS + 0.12, z: 0 },
    fanBalls: [],
    records: [],
    bodyIds: new Int32Array(0),
    transforms: new Float32Array(0),
    idsDirty: true,
    nextKickAt: 4.8,
    kick: createIdleKickState(),
  };

  addStaticColliders(created);
  return created;
}

function addStaticColliders(rt: CupPhysicsRuntime) {
  addStaticBox(rt, 0, -0.1, 0, 17.4, 0.1, 21.4, 0.88, 0.05);
  addStaticBox(rt, 0, -0.03, 0, 9.25, 0.08, 14.25, 0.92, 0.04);

  addStaticBox(rt, -9.42, 0.48, 0, 0.18, 0.56, 14.5, 0.72, 0.18);
  addStaticBox(rt, 9.42, 0.48, 0, 0.18, 0.56, 14.5, 0.72, 0.18);
  addStaticBox(rt, 0, 0.48, -14.42, 9.45, 0.56, 0.18, 0.72, 0.18);
  addStaticBox(rt, 0, 0.48, 14.42, 9.45, 0.56, 0.18, 0.72, 0.18);

  addStaticBox(rt, -17.25, 1.4, 0, 0.28, 1.6, 21.5, 0.7, 0.18);
  addStaticBox(rt, 17.25, 1.4, 0, 0.28, 1.6, 21.5, 0.7, 0.18);
  addStaticBox(rt, 0, 1.4, -21.25, 17.4, 1.6, 0.28, 0.7, 0.18);
  addStaticBox(rt, 0, 1.4, 21.25, 17.4, 1.6, 0.28, 0.7, 0.18);

  for (let tier = 0; tier < 3; tier += 1) {
    const y = 0.5 + tier * 0.58;
    const x = 11.4 + tier * 1.35;
    const z = 18.0 + tier * 1.35;
    addStaticBox(rt, -x, y, 0, 1.05, 0.43, 17.5, 0.82, 0.12);
    addStaticBox(rt, x, y, 0, 1.05, 0.43, 17.5, 0.82, 0.12);
    addStaticBox(rt, 0, y, -z, 14, 0.43, 1.05, 0.82, 0.12);
    addStaticBox(rt, 0, y, z, 14, 0.43, 1.05, 0.82, 0.12);
  }
}

function addStaticBox(
  rt: CupPhysicsRuntime,
  x: number,
  y: number,
  z: number,
  hx: number,
  hy: number,
  hz: number,
  friction: number,
  restitution: number,
) {
  const bodyId = rt.world.createBody({ type: 'static', position: [x, y, z] });
  rt.world.addBox(bodyId, [hx, hy, hz], { density: 0, friction, restitution });
}

function createIdleKickState(): CupKickState {
  return {
    active: false,
    playerIndex: -1,
    phase: 'idle',
    startedAt: 0,
    strikeAt: 0,
    recoverStartedAt: 0,
    recoverFromX: 0,
    recoverFromZ: 0,
    recoverFromYaw: 0,
    strikeApplied: false,
    nearestPlayer: { x: 0, y: PLAYER_BASE_Y + PLAYER_BODY_CENTER_OFFSET_Y, z: 0 },
    ballAtStrike: null,
  };
}

function spawnDueIntroBoxes(rt: CupPhysicsRuntime) {
  if (rt.introBodiesDestroyed || rt.introPhase === 'collapsing' || rt.introPhase === 'done') return;

  let spawned = false;
  for (const box of rt.introBoxes) {
    if (box.bodyId || rt.elapsed < box.delay) continue;
    const bodyId = rt.world.createBody({
      type: 'kinematic',
      position: [box.startX, box.startY, box.startZ],
    });
    rt.world.addBox(bodyId, [box.hx, box.hy, box.hz], { density: 0.72, friction: 0.86, restitution: 0.08 });
    box.bodyId = bodyId;
    box.dynamic = false;
    box.currentX = box.startX;
    box.currentY = box.startY;
    box.currentZ = box.startZ;
    box.currentQX = 0;
    box.currentQY = 0;
    box.currentQZ = 0;
    box.currentQW = 1;
    spawned = true;
  }

  if (spawned) {
    rt.idsDirty = true;
  }
}

function updateIntroLifecycle(rt: CupPhysicsRuntime) {
  if (rt.introBodiesDestroyed) return;

  if (rt.introPhase === 'assembling' && rt.elapsed >= INTRO_HOLD_START) {
    rt.introPhase = 'holding';
  }

  if (!rt.introFadeStarted && rt.elapsed >= INTRO_COLLAPSE_START) {
    rt.introPhase = 'collapsing';
    rt.introFadeStarted = true;
    rt.introFadeStartedAt = rt.elapsed;
    releaseIntroBoxes(rt);
  }

  if (rt.introFadeStarted) {
    rt.introFade = clamp(1 - (rt.elapsed - rt.introFadeStartedAt) / INTRO_FADE_SECONDS, 0, 1);
    if (rt.introFade <= 0) {
      rt.introBoxes.forEach((box) => {
        if (box.bodyId) {
          rt.world.destroyBody(box.bodyId as BodyHandle);
          box.bodyId = 0;
        }
      });
      rt.introBodiesDestroyed = true;
      rt.introPhase = 'done';
      rt.lineupVisible = true;
      rt.idsDirty = true;
      ensureAmbientBodies(rt);
    }
  }
}

function updateIntroKinematicTargets(rt: CupPhysicsRuntime, dt: number) {
  if (rt.introPhase !== 'assembling' && rt.introPhase !== 'holding') return;

  for (const box of rt.introBoxes) {
    if (!box.bodyId || box.dynamic) continue;
    const progress = clamp((rt.elapsed - box.delay) / INTRO_ASSEMBLE_SECONDS, 0, 1);
    const eased = easeOutCubic(progress);
    const x = lerp(box.startX, box.targetX, eased);
    const y = lerp(box.startY, box.targetY, eased);
    const z = lerp(box.startZ, box.targetZ, eased);
    rt.world.setKinematicTarget(box.bodyId as BodyHandle, [x, y, z], [0, 0, 0, 1], dt);
  }
}

function releaseIntroBoxes(rt: CupPhysicsRuntime) {
  let released = false;
  rt.introBoxes.forEach((box, index) => {
    if (!box.bodyId || box.dynamic) return;

    const x = box.currentX;
    const y = box.currentY;
    const z = box.currentZ;
    const qx = box.currentQX;
    const qy = box.currentQY;
    const qz = box.currentQZ;
    const qw = box.currentQW;
    rt.world.destroyBody(box.bodyId as BodyHandle);
    const bodyId = rt.world.createBody({
      type: 'dynamic',
      position: [x, y, z],
      rotation: [qx, qy, qz, qw],
      ccd: true,
    });
    rt.world.addBox(bodyId, [box.hx, box.hy, box.hz], { density: 0.72, friction: 0.86, restitution: 0.18 });
    rt.world.setLinearVelocity(bodyId, [
      (seededUnit(index + 229) - 0.5) * 0.8,
      -0.2 - seededUnit(index + 241) * 0.45,
      (seededUnit(index + 257) - 0.5) * 0.9,
    ]);
    box.bodyId = bodyId;
    box.dynamic = true;
    released = true;
  });

  if (released) {
    rt.idsDirty = true;
  }
}

function ensureAmbientBodies(rt: CupPhysicsRuntime) {
  if (rt.ambientReady) return;

  rt.playerBodies = STADIUM_PLAYER_LAYOUT.map((_layout, index) => {
    const pose = stadiumPlayerPhysicsPose(index, rt.fixedTime, rt);
    const bodyId = rt.world.createBody({
      type: 'kinematic',
      position: [pose.x, pose.y, pose.z],
      rotation: [pose.qx, pose.qy, pose.qz, pose.qw],
    });
    rt.world.addCapsule(bodyId, 0.28, 0.55, { density: 0, friction: 0.76, restitution: 0.14 });
    return bodyId;
  });

  rt.matchBallBody = rt.world.createBody({
    type: 'dynamic',
    position: [0, MATCH_BALL_RADIUS + 0.18, 0],
    ccd: true,
  });
  rt.world.addSphere(rt.matchBallBody as BodyHandle, MATCH_BALL_RADIUS, { density: 0.55, friction: 0.48, restitution: 0.55 });
  rt.ambientReady = true;
  rt.nextKickAt = rt.fixedTime + 1.2 + nextRandom(rt) * 1.4;
  rt.idsDirty = true;
}

function updatePlayerTargets(rt: CupPhysicsRuntime, dt: number) {
  rt.playerBodies.forEach((bodyId, index) => {
    const pose = stadiumPlayerPhysicsPose(index, rt.fixedTime, rt);
    rt.world.setKinematicTarget(bodyId as BodyHandle, [pose.x, pose.y, pose.z], [pose.qx, pose.qy, pose.qz, pose.qw], dt);
  });
}

function updateKickChoreography(rt: CupPhysicsRuntime) {
  const kick = rt.kick;
  if (!rt.matchBallBody) return;

  if (!kick.active && rt.fixedTime >= rt.nextKickAt) {
    startKickSequence(rt);
  }

  if (!kick.active) {
    kick.phase = 'idle';
    return;
  }

  const activePose = stadiumPlayerPhysicsPose(kick.playerIndex, rt.fixedTime, rt);
  kick.nearestPlayer = { x: activePose.x, y: activePose.y, z: activePose.z };

  if (kick.phase === 'lunge' && rt.fixedTime >= kick.strikeAt) {
    strikeMatchBall(rt, activePose);
    kick.phase = 'strike';
    kick.strikeApplied = true;
    kick.recoverStartedAt = rt.fixedTime + KICK_STRIKE_SECONDS;
    kick.recoverFromX = activePose.x;
    kick.recoverFromZ = activePose.z;
    kick.recoverFromYaw = activePose.yaw;
    return;
  }

  if (kick.phase === 'strike' && rt.fixedTime >= kick.recoverStartedAt) {
    kick.phase = 'recover';
  }

  if (kick.phase === 'recover' && rt.fixedTime >= kick.recoverStartedAt + KICK_RECOVER_SECONDS) {
    kick.active = false;
    kick.phase = 'idle';
  }
}

function startKickSequence(rt: CupPhysicsRuntime) {
  const ball = rt.matchBallPosition;
  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  STADIUM_PLAYER_LAYOUT.forEach((layout, index) => {
    const dx = layout.x - ball.x;
    const dz = layout.z - ball.z;
    const distance = dx * dx + dz * dz;
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });

  rt.kick = {
    active: true,
    playerIndex: nearestIndex,
    phase: 'lunge',
    startedAt: rt.fixedTime,
    strikeAt: rt.fixedTime + KICK_LUNGE_SECONDS,
    recoverStartedAt: 0,
    recoverFromX: STADIUM_PLAYER_LAYOUT[nearestIndex].x,
    recoverFromZ: STADIUM_PLAYER_LAYOUT[nearestIndex].z,
    recoverFromYaw: STADIUM_PLAYER_LAYOUT[nearestIndex].rotation,
    strikeApplied: false,
    nearestPlayer: {
      x: STADIUM_PLAYER_LAYOUT[nearestIndex].x,
      y: PLAYER_BASE_Y + PLAYER_BODY_CENTER_OFFSET_Y,
      z: STADIUM_PLAYER_LAYOUT[nearestIndex].z,
    },
    ballAtStrike: null,
  };
}

function strikeMatchBall(rt: CupPhysicsRuntime, playerPose: ReturnType<typeof stadiumPlayerPhysicsPose>) {
  const ball = rt.matchBallPosition;
  const nearestIndex = rt.kick.playerIndex;

  const targetStart = nearestIndex < lineupXs.length ? lineupXs.length : 0;
  const targetIndex = targetStart + Math.floor(nextRandom(rt) * lineupXs.length) % lineupXs.length;
  const target = STADIUM_PLAYER_LAYOUT[targetIndex];
  let dx = target.x - ball.x + (nextRandom(rt) - 0.5) * 0.8;
  let dz = target.z - ball.z + (nextRandom(rt) - 0.5) * 0.8;
  const length = Math.hypot(dx, dz) || 1;
  dx /= length;
  dz /= length;

  // Set velocity directly rather than applyImpulse: the ball is deliberately light
  // (density 0.55, r=0.28 -> ~0.05kg), so an impulse tuned for a ~1kg ball imparts
  // 100+ m/s and launches it out of the stadium. A direct velocity is mass-independent
  // and gives a controllable lofted pass that lands back on the pitch.
  const speed = clamp(length * 0.55, 3.2, 6.0);
  rt.world.setLinearVelocity(rt.matchBallBody as BodyHandle, [
    dx * speed,
    2.4 + nextRandom(rt) * 1.1,
    dz * speed,
  ]);
  rt.kick.nearestPlayer = { x: playerPose.x, y: playerPose.y, z: playerPose.z };
  rt.kick.ballAtStrike = { x: ball.x, y: ball.y, z: ball.z };
  rt.matchBallPulse = 0.26;
  rt.nextKickAt = rt.fixedTime + 3 + nextRandom(rt) * 3;
}

function pulseContactedBalls(rt: CupPhysicsRuntime) {
  const events = rt.world.drainContactBeginEvents();
  for (const event of events) {
    if (event.approachSpeed < 0.35) continue;
    if (event.bodyA === rt.matchBallBody || event.bodyB === rt.matchBallBody) {
      rt.matchBallPulse = Math.max(rt.matchBallPulse, Math.min(0.22, event.approachSpeed * 0.018));
    }
    for (const ball of rt.fanBalls) {
      if (event.bodyA === ball.bodyId || event.bodyB === ball.bodyId) {
        ball.pulse = Math.max(ball.pulse, Math.min(0.24, event.approachSpeed * 0.02));
      }
    }
  }
}

function updateCachedMatchBallPosition(rt: CupPhysicsRuntime) {
  if (!rt.matchBallBody) return;

  for (let i = 0; i < rt.records.length; i += 1) {
    const record = rt.records[i];
    if (record.kind !== 'matchBall') continue;
    const offset = i * 7;
    rt.matchBallPosition.x = rt.transforms[offset];
    rt.matchBallPosition.y = rt.transforms[offset + 1];
    rt.matchBallPosition.z = rt.transforms[offset + 2];
    return;
  }
}

function updateCachedIntroBoxTransforms(rt: CupPhysicsRuntime) {
  for (let i = 0; i < rt.records.length; i += 1) {
    const record = rt.records[i];
    if (record.kind !== 'introBox') continue;
    const box = rt.introBoxes[record.index];
    if (!box) continue;
    const offset = i * 7;
    box.currentX = rt.transforms[offset];
    box.currentY = rt.transforms[offset + 1];
    box.currentZ = rt.transforms[offset + 2];
    box.currentQX = rt.transforms[offset + 3];
    box.currentQY = rt.transforms[offset + 4];
    box.currentQZ = rt.transforms[offset + 5];
    box.currentQW = rt.transforms[offset + 6];
  }
}

function rebuildReadBuffers(rt: CupPhysicsRuntime) {
  const records: PhysicsReadRecord[] = [];
  rt.introBoxes.forEach((box, index) => {
    if (box.bodyId) {
      records.push({ bodyId: box.bodyId, kind: 'introBox', index });
    }
  });
  if (rt.matchBallBody) {
    records.push({ bodyId: rt.matchBallBody, kind: 'matchBall', index: 0 });
  }
  rt.fanBalls.forEach((ball, index) => {
    records.push({ bodyId: ball.bodyId, kind: 'fanBall', index });
  });

  rt.records = records;

  // Back the read path with a fresh TransformBuffer in record order so the
  // flat rt.transforms offsets (record index * 7) stay stable.
  rt.buffer = new TransformBuffer(Math.max(records.length, 1));
  for (const record of records) rt.buffer.add(record.bodyId as BodyHandle);
  rt.buffer.rebuild();
  rt.bodyIds = rt.buffer.ids;
  rt.transforms = rt.buffer.transforms;
  rt.idsDirty = false;
}

export function stadiumPlayerRenderPose(index: number, elapsed: number, rt: CupPhysicsRuntime | null = null) {
  const pose = stadiumPlayerPhysicsPose(index, elapsed, rt);
  return {
    x: pose.x,
    y: pose.y - PLAYER_BODY_CENTER_OFFSET_Y,
    z: pose.z,
    rotation: pose.yaw,
  };
}

export function stadiumPlayerPhysicsPose(index: number, elapsed: number, rt: CupPhysicsRuntime | null = null) {
  const layout = STADIUM_PLAYER_LAYOUT[index];
  let x = layout.x;
  let z = layout.z;
  let yaw = layout.rotation;
  const kick = rt?.kick;

  if (rt?.ambientReady && kick?.active && kick.playerIndex === index) {
    if (kick.phase === 'lunge') {
      const approach = kickApproachPose(layout, rt.matchBallPosition);
      const t = easeInOutCubic(clamp((elapsed - kick.startedAt) / KICK_LUNGE_SECONDS, 0, 1));
      x = lerp(layout.x, approach.x, t);
      z = lerp(layout.z, approach.z, t);
      yaw = lerpAngle(layout.rotation, approach.yaw, t);
    } else if (kick.phase === 'strike') {
      x = kick.recoverFromX;
      z = kick.recoverFromZ;
      yaw = kick.recoverFromYaw;
    } else if (kick.phase === 'recover') {
      const t = easeInOutCubic(clamp((elapsed - kick.recoverStartedAt) / KICK_RECOVER_SECONDS, 0, 1));
      x = lerp(kick.recoverFromX, layout.x, t);
      z = lerp(kick.recoverFromZ, layout.z, t);
      yaw = lerpAngle(kick.recoverFromYaw, layout.rotation, t);
    }
  }

  const halfYaw = yaw / 2;
  return {
    x,
    y: stadiumPlayerGroupY(layout, elapsed) + PLAYER_BODY_CENTER_OFFSET_Y,
    z,
    qx: 0,
    qy: Math.sin(halfYaw),
    qz: 0,
    qw: Math.cos(halfYaw),
    yaw,
  };
}

function kickApproachPose(
  layout: StadiumPlayerLayout,
  ball: { x: number; y: number; z: number },
) {
  const dx = ball.x - layout.x;
  const dz = ball.z - layout.z;
  const length = Math.hypot(dx, dz);
  if (length < 0.001) {
    return { x: layout.x, z: layout.z, yaw: layout.rotation };
  }

  const stopShort = 0.55;
  const travel = Math.max(0, length - stopShort);
  const nx = dx / length;
  const nz = dz / length;
  return {
    x: clamp(layout.x + nx * travel, -8.75, 8.75),
    z: clamp(layout.z + nz * travel, -13.9, 13.9),
    yaw: Math.atan2(dx, dz),
  };
}

function buildIntroBoxSpecs() {
  const glyphs = [
    ['1111', '1...', '1...', '1...', '1...', '1111'],
    ['1..1', '1..1', '1..1', '1..1', '1..1', '.11.'],
    ['111.', '1..1', '1..1', '111.', '1...', '1...'],
    ['1111', '...1', '...1', '1111', '1...', '1111'],
    ['1111', '1...', '1...', '1111', '1..1', '1111'],
  ];
  const block = 0.58;
  const gap = 0.72;
  const charWidth = 4 * block;
  const wordSpace = charWidth * 1.2;
  const totalWidth = glyphs.length * charWidth + (glyphs.length - 1) * gap + wordSpace;
  const xOrigin = -totalWidth / 2 + block / 2;
  const bottomY = 0.42;
  const topY = bottomY + (glyphs[0].length - 1) * block;
  const specs: IntroBoxSpec[] = [];

  let glyphX = xOrigin;
  glyphs.forEach((rows, glyphIndex) => {
    rows.forEach((row, rowIndex) => {
      [...row].forEach((cell, colIndex) => {
        if (cell !== '1') return;
        const index = specs.length;
        const targetX = glyphX + colIndex * block;
        const targetY = topY - rowIndex * block;
        const targetZ = (seededUnit(index + 93) - 0.5) * 0.045;
        specs.push({
          startX: targetX + (seededUnit(index + 5) - 0.5) * 2.3,
          startY: targetY + 6.4 + seededUnit(index + 71) * 2.2 + glyphIndex * 0.18,
          startZ: targetZ + (seededUnit(index + 17) - 0.5) * 1.4 + 0.35,
          targetX,
          targetY,
          targetZ,
          hx: 0.265,
          hy: 0.18,
          hz: 0.265,
          delay: Math.min(1.2, glyphIndex * 0.17 + rowIndex * 0.035 + seededUnit(index + 113) * 0.2),
          color: index % 3 === 0 ? '#fff7dd' : '#f4c95d',
        });
      });
    });
    glyphX += charWidth + gap + (glyphIndex === 2 ? wordSpace : 0);
  });

  return specs;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpAngle(a: number, b: number, t: number) {
  return a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;
}

function easeOutCubic(t: number) {
  return 1 - (1 - t) ** 3;
}

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
}

function seededUnit(seed: number) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function nextRandom(rt: CupPhysicsRuntime) {
  rt.randomSeed = (rt.randomSeed * 1664525 + 1013904223) >>> 0;
  return rt.randomSeed / 0x100000000;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
